import React from "react";
import PropTypes from "prop-types";
import { Field } from "formik";
import { Col, FormGroup, Row } from "reactstrap";
import classNames from "classnames";
import flatten from "lodash/flatten"; // Array.flat and Array.flatMap aren't fully supported yet

import FormRowField from "shared/react_components/FormRowField";
import { Select } from "shared/formik/formik_helpers";
import AddList from "shared/react_components/AddList";
import { SlotContent } from "shared/react_components/slots";
import { findOptionWhenGrouped } from "shared/utils/select_helpers";

import FilterValues from "./FilterValues";

import { sectionHeader } from "./styles.module.scss";

const MIN_FILTERS_TO_ADD_NEW = 1;

const FilterAddList = ({ fieldName, filterOptions, currentDateRange, leftHeader, rightHeader, customAddNewMessage, minItemsCount }) => (
  <FormGroup name={fieldName} className="sb-field-group">
    <Field name={fieldName}>
      {({ field, form }) => {
        const currentFilterKeys = field.value.map(value => value.filterKey);
        const selectFilterOptions = filterOptions.map(({ label, options }) => ({
          label,
          options: options.filter(
            filter => !currentFilterKeys.includes(filter.value)
          )
        }));
        const selectFilterOptionsLength = flatten(selectFilterOptions.map(({ options }) => options)).length;
        const filterOptionsValues = flatten(filterOptions.map(({ options }) => options));

        const getOptionType = (filterKey) => {
          const currentOption = filterOptionsValues.filter(filter => filter.value === filterKey);
          return currentOption.length ? currentOption[0].type : "";
        };

        return (
          <>
            {(leftHeader || rightHeader) && !!field.value.length && (
              <AddList.ItemLayout>
                <SlotContent name={"item"}>
                  <Row className="pt-3 pl-2" data-selector="headers">
                    <Col xs={6}>
                      <div className={classNames(sectionHeader, "pb-1")}>{leftHeader}</div>
                    </Col>
                    <Col xs={6}>
                      <div className={classNames(sectionHeader, "pb-1")}>{rightHeader}</div>
                    </Col>
                  </Row>
                </SlotContent>
              </AddList.ItemLayout>
            )}
            <AddList
              items={field.value}
              emptyItem={{ filterKey: "", filterValues: [] }}
              onChange={value => form.setFieldValue(field.name, value)}
              customAddNewMessage={customAddNewMessage}
              addButtonVisible={selectFilterOptionsLength >= MIN_FILTERS_TO_ADD_NEW}
              minItemsCount={minItemsCount}
            >
              {({ item, fieldUpdater, index }) => (
                <Row className="pl-2">
                  <Col xs={6}>
                    <FormRowField name={`${fieldName}[${index}].filterKey`} dblockErrors>
                      {({ field, form, ...props }) => (
                        <Select
                          {...props}
                          field={field}
                          form={form}
                          value={findOptionWhenGrouped(filterOptions, item.filterKey)}
                          options={selectFilterOptions}
                          onChange={({ value }) => fieldUpdater("filterKey")(value)}
                          onBlur={() => form.setFieldTouched(field.name, true)}
                          grouped={true}
                        />
                      )}
                    </FormRowField>
                  </Col>
                  <Col xs={6}>
                    <FilterValues
                      fieldName={fieldName}
                      item={item}
                      currentDateRange={currentDateRange}
                      filterType={getOptionType(item.filterKey)}
                      form={form}
                      fieldUpdater={fieldUpdater}
                      index={index}
                    />
                  </Col>
                </Row>
              )}
            </AddList>
          </>
        );
      }}
    </Field>
  </FormGroup>
);

FilterAddList.propTypes = {
  fieldName: PropTypes.string.isRequired,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
          type: PropTypes.string
        })
      ).isRequired
    })
  ).isRequired,
  currentDateRange: PropTypes.string.isRequired,
  leftHeader: PropTypes.string,
  rightHeader: PropTypes.string,
  customAddNewMessage: PropTypes.string,
  minItemsCount: PropTypes.number
};

FilterAddList.defaultProps = {
  minItemsCount: 0
};

export default FilterAddList;
